import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { ArrowLeft, DollarSign, Users, Wallet, RefreshCw, Crown, Shield, User, Zap, Clock, Play, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppBranding from './AppBranding';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const ACCESS_LEVEL_ICONS = {
  FREE: <User className="h-4 w-4" />,
  PAID: <DollarSign className="h-4 w-4" />,
  FAMILY: <Users className="h-4 w-4" />,
  ADMIN: <Shield className="h-4 w-4" />,
  GOD: <Crown className="h-4 w-4" />
};

const ACCESS_LEVEL_COLORS = {
  FREE: 'bg-gray-600',
  PAID: 'bg-blue-600',
  FAMILY: 'bg-purple-600',
  ADMIN: 'bg-yellow-600',
  GOD: 'bg-red-600'
};

const AdminMonetizationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState({ simulation_wallet: '', live_wallet: '' });
  const [siphonStats, setSiphonStats] = useState(null);
  const [usersByLevel, setUsersByLevel] = useState({});
  const [accessLevels, setAccessLevels] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [newLevel, setNewLevel] = useState('');
  const [myLevel, setMyLevel] = useState('FREE');
  const [sweepConfig, setSweepConfig] = useState(null);
  const [sweepHistory, setSweepHistory] = useState([]);
  const [thresholdInput, setThresholdInput] = useState('');
  const [sweepLoading, setSweepLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [walletsRes, statsRes, usersRes, levelsRes, myLevelRes, sweepCfgRes, sweepHistRes] = await Promise.all([
        axios.get(`${API_V2}/monetization/admin/wallets`, { withCredentials: true }),
        axios.get(`${API_V2}/monetization/siphon/stats`, { withCredentials: true }),
        axios.get(`${API_V2}/monetization/users/by-level`, { withCredentials: true }),
        axios.get(`${API_V2}/monetization/access-levels`, { withCredentials: true }),
        axios.get(`${API_V2}/monetization/my-level`, { withCredentials: true }),
        axios.get(`${API_V2}/monetization/admin/sweep/config`, { withCredentials: true }).catch(() => null),
        axios.get(`${API_V2}/monetization/admin/sweep/history`, { withCredentials: true }).catch(() => null),
      ]);

      if (walletsRes.data.success) setWallets(walletsRes.data);
      if (statsRes.data.success) setSiphonStats(statsRes.data);
      if (usersRes.data.success) setUsersByLevel(usersRes.data.users_by_level);
      if (levelsRes.data.success) setAccessLevels(levelsRes.data.access_levels);
      if (myLevelRes.data.success) setMyLevel(myLevelRes.data.access_level);
      if (sweepCfgRes?.data?.success) {
        setSweepConfig(sweepCfgRes.data);
        setThresholdInput(String(sweepCfgRes.data.min_transfer_threshold));
      }
      if (sweepHistRes?.data?.success) setSweepHistory(sweepHistRes.data.payouts || []);
    } catch (error) {
      console.error('Failed to fetch monetization data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin or GOD access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveWallets = async () => {
    try {
      await axios.post(`${API_V2}/monetization/admin/wallets`, wallets, { withCredentials: true });
      toast.success('Payment wallets saved');
    } catch (error) {
      toast.error('Failed to save wallets');
    }
  };

  const handleSetUserLevel = async () => {
    if (!selectedUser || !newLevel) {
      toast.error('Select a user and access level');
      return;
    }

    try {
      const response = await axios.post(
        `${API_V2}/monetization/set-level/${selectedUser.user_id}`,
        { access_level: newLevel },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(`${selectedUser.name} set to ${newLevel}`);
        setSelectedUser(null);
        setNewLevel('');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set access level');
    }
  };

  const handleSaveThreshold = async () => {
    const val = parseFloat(thresholdInput);
    if (isNaN(val) || val < 0) {
      toast.error('Enter a valid non-negative threshold');
      return;
    }
    try {
      await axios.post(`${API_V2}/monetization/admin/sweep/config`, { min_transfer_threshold: val }, { withCredentials: true });
      toast.success(`Sweep threshold set to $${val.toFixed(2)}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to save threshold');
    }
  };

  const handleTriggerSweep = async (force) => {
    setSweepLoading(true);
    try {
      const res = await axios.post(`${API_V2}/monetization/admin/sweep/trigger`, { force }, { withCredentials: true });
      const d = res.data;
      if (d.status === 'deferred') {
        toast.info(`Sweep deferred — $${d.total_amount?.toFixed(8)} pending, below $${d.threshold?.toFixed(2)} threshold`);
      } else if (d.status === 'no_pending') {
        toast.info('No pending fees to sweep');
      } else {
        toast.success(`Swept ${d.transferred} transactions totaling $${d.total_amount?.toFixed(8)}`);
      }
      fetchData();
    } catch (error) {
      toast.error('Sweep failed');
    } finally {
      setSweepLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-[#00FF41] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-[#00FF41]">
      <AppBranding />
      <div className="p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#00FF41] hover:bg-[#003B00]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold tracking-wider">MONETIZATION ADMIN</h1>
            <Badge className={`${ACCESS_LEVEL_COLORS[myLevel]} text-white`}>
              {ACCESS_LEVEL_ICONS[myLevel]} {myLevel}
            </Badge>
          </div>
          <Button onClick={fetchData} variant="ghost" className="text-[#00FF41]">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment Wallets */}
          <Card className="bg-[#0a0a0a] border-[#003B00]">
            <CardHeader>
              <CardTitle className="text-[#00FF41] flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                PAYMENT WALLETS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-[#008F11]">Simulation Wallet (BTC Address)</label>
                <Input
                  data-testid="sim-wallet-input"
                  value={wallets.simulation_wallet || ''}
                  onChange={(e) => setWallets({ ...wallets, simulation_wallet: e.target.value })}
                  placeholder="Enter simulation BTC wallet address"
                  className="bg-[#001a00] border-[#003B00] text-[#00FF41] font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">Fees from simulation trades go here</p>
              </div>
              <div>
                <label className="text-sm text-[#008F11]">Live Wallet (BTC Address)</label>
                <Input
                  data-testid="live-wallet-input"
                  value={wallets.live_wallet || ''}
                  onChange={(e) => setWallets({ ...wallets, live_wallet: e.target.value })}
                  placeholder="Enter live BTC wallet address"
                  className="bg-[#001a00] border-[#003B00] text-[#00FF41] font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">Fees from live trades go here</p>
              </div>
              <Button data-testid="save-wallets-btn" onClick={handleSaveWallets} className="w-full bg-[#003B00] hover:bg-[#004d00] text-[#00FF41]">
                Save Wallets
              </Button>
            </CardContent>
          </Card>

          {/* Siphon Stats */}
          <Card className="bg-[#0a0a0a] border-[#003B00]">
            <CardHeader>
              <CardTitle className="text-[#00FF41] flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                SIPHON STATISTICS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#001a00] border border-[#003B00]">
                  <p className="text-xs text-[#008F11]">SIMULATION</p>
                  <p className="text-2xl font-bold text-cyan-400">
                    ${(siphonStats?.simulation?.total_collected || 0).toFixed(6)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {siphonStats?.simulation?.total_transactions || 0} transactions
                  </p>
                </div>
                <div className="p-4 bg-[#001a00] border border-[#003B00]">
                  <p className="text-xs text-[#008F11]">LIVE</p>
                  <p className="text-2xl font-bold text-[#00FF41]">
                    ${(siphonStats?.live?.total_collected || 0).toFixed(6)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {siphonStats?.live?.total_transactions || 0} transactions
                  </p>
                </div>
              </div>
              
              <Separator className="bg-[#003B00]" />
              
              <div>
                <p className="text-sm text-[#008F11] mb-2">Recent Transactions</p>
                <ScrollArea className="h-32">
                  {[...(siphonStats?.simulation?.recent_transactions || []), ...(siphonStats?.live?.recent_transactions || [])]
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .slice(0, 10)
                    .map((t, i) => (
                      <div key={i} className="flex justify-between text-xs py-1 border-b border-[#003B00]/50">
                        <span className={t.mode === 'live' ? 'text-[#00FF41]' : 'text-cyan-400'}>
                          {t.mode.toUpperCase()}
                        </span>
                        <span>${t.fee_amount?.toFixed(8)}</span>
                        <span className="text-gray-500">{t.pair}</span>
                      </div>
                    ))}
                </ScrollArea>
              </div>
            </CardContent>
          </Card>

          {/* Daily Sweep Configuration */}
          <Card className="bg-[#0a0a0a] border-[#003B00] lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-[#00FF41] flex items-center gap-2">
                <Clock className="h-5 w-5" />
                DAILY SIPHON SWEEP
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Pending Balance */}
                <div className="p-4 bg-[#001a00] border border-[#003B00]">
                  <p className="text-xs text-[#008F11]">PENDING BALANCE</p>
                  <p data-testid="pending-balance" className="text-2xl font-bold text-yellow-400">
                    ${(sweepConfig?.pending_balance || 0).toFixed(8)}
                  </p>
                  <p className="text-xs text-gray-500">{sweepConfig?.pending_count || 0} pending transactions</p>
                </div>
                {/* Threshold */}
                <div className="p-4 bg-[#001a00] border border-[#003B00]">
                  <p className="text-xs text-[#008F11]">TX COST THRESHOLD</p>
                  <p data-testid="sweep-threshold" className="text-2xl font-bold text-[#00FF41]">
                    ${(sweepConfig?.min_transfer_threshold || 5).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {sweepConfig?.would_transfer
                      ? <span className="text-green-400">Ready to transfer</span>
                      : <span className="text-yellow-400">Accumulating fees...</span>
                    }
                  </p>
                </div>
                {/* Lifetime Sweeps */}
                <div className="p-4 bg-[#001a00] border border-[#003B00]">
                  <p className="text-xs text-[#008F11]">TOTAL SWEPT</p>
                  <p className="text-2xl font-bold text-cyan-400">
                    ${(sweepConfig?.total_swept_amount || 0).toFixed(8)}
                  </p>
                  <p className="text-xs text-gray-500">{sweepConfig?.total_sweeps || 0} sweeps completed</p>
                </div>
              </div>

              <Separator className="bg-[#003B00]" />

              {/* Threshold Configuration */}
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-sm text-[#008F11]">Min Transfer Threshold ($)</label>
                  <Input
                    data-testid="threshold-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={thresholdInput}
                    onChange={(e) => setThresholdInput(e.target.value)}
                    placeholder="5.00"
                    className="bg-[#001a00] border-[#003B00] text-[#00FF41] font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">Fees below this amount will wait until the next day</p>
                </div>
                <Button data-testid="save-threshold-btn" onClick={handleSaveThreshold} className="bg-[#003B00] hover:bg-[#004d00] text-[#00FF41]">
                  Save
                </Button>
              </div>

              {/* Manual Sweep Buttons */}
              <div className="flex gap-3">
                <Button
                  data-testid="sweep-btn"
                  onClick={() => handleTriggerSweep(false)}
                  disabled={sweepLoading}
                  className="flex-1 bg-[#003B00] hover:bg-[#004d00] text-[#00FF41]"
                >
                  <Play className="h-4 w-4 mr-2" />
                  {sweepLoading ? 'Running...' : 'Run Sweep (with threshold check)'}
                </Button>
                <Button
                  data-testid="force-sweep-btn"
                  onClick={() => handleTriggerSweep(true)}
                  disabled={sweepLoading}
                  variant="destructive"
                  className="bg-red-900 hover:bg-red-800 text-red-200"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Force Sweep
                </Button>
              </div>

              {/* Sweep History */}
              {sweepHistory.length > 0 && (
                <>
                  <Separator className="bg-[#003B00]" />
                  <div>
                    <p className="text-sm text-[#008F11] mb-2">Sweep History</p>
                    <ScrollArea className="h-36">
                      {sweepHistory.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-2 border-b border-[#003B00]/50">
                          <span className="text-gray-400 w-36">
                            {new Date(p.swept_at).toLocaleString()}
                          </span>
                          <Badge
                            data-testid={`sweep-status-${i}`}
                            className={
                              p.status === 'completed' ? 'bg-green-800 text-green-200' :
                              p.status === 'deferred' ? 'bg-yellow-800 text-yellow-200' :
                              'bg-gray-700 text-gray-300'
                            }
                          >
                            {p.status}
                          </Badge>
                          <span className="font-mono text-[#00FF41]">${p.total_amount?.toFixed(8)}</span>
                          <span className="text-gray-500">{p.transaction_count} txns</span>
                          {p.forced && <span className="text-red-400 text-[10px]">FORCED</span>}
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          <Card className="bg-[#0a0a0a] border-[#003B00]">
            <CardHeader>
              <CardTitle className="text-[#00FF41] flex items-center gap-2">
                <Zap className="h-5 w-5" />
                FEE STRUCTURE
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(accessLevels).map(([level, info]) => (
                  <div key={level} className="flex items-center justify-between p-2 bg-[#001a00] border border-[#003B00]">
                    <div className="flex items-center gap-2">
                      <Badge className={`${ACCESS_LEVEL_COLORS[level]} text-white`}>
                        {ACCESS_LEVEL_ICONS[level]} {level}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono">
                        {info.fee_percent === 0 ? 'NO FEE' : `${info.fee_percent}%`}
                      </p>
                      {info.subscription_cost > 0 && (
                        <p className="text-xs text-yellow-400">${info.subscription_cost}/mo</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* User Management */}
          <Card className="bg-[#0a0a0a] border-[#003B00]">
            <CardHeader>
              <CardTitle className="text-[#00FF41] flex items-center gap-2">
                <Users className="h-5 w-5" />
                USER ACCESS LEVELS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Set User Level */}
              <div className="p-4 bg-[#001a00] border border-[#003B00] space-y-3">
                <p className="text-sm text-[#008F11]">Change User Access Level</p>
                <Select onValueChange={(val) => {
                  const allUsers = Object.values(usersByLevel).flat();
                  const user = allUsers.find(u => u.user_id === val);
                  setSelectedUser(user);
                }}>
                  <SelectTrigger className="bg-[#001a00] border-[#003B00] text-[#00FF41]">
                    <SelectValue placeholder="Select user..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#003B00]">
                    {Object.values(usersByLevel).flat().map(user => (
                      <SelectItem key={user.user_id} value={user.user_id} className="text-[#00FF41]">
                        {user.name || user.email} ({user.access_level || 'FREE'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={newLevel} onValueChange={setNewLevel}>
                  <SelectTrigger className="bg-[#001a00] border-[#003B00] text-[#00FF41]">
                    <SelectValue placeholder="Select new level..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#003B00]">
                    {Object.keys(accessLevels).map(level => (
                      <SelectItem key={level} value={level} className="text-[#00FF41]">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={handleSetUserLevel} 
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-black"
                  disabled={!selectedUser || !newLevel}
                >
                  Set Access Level
                </Button>
              </div>

              {/* Users by Level */}
              <ScrollArea className="h-48">
                {Object.entries(usersByLevel).map(([level, users]) => (
                  <div key={level} className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`${ACCESS_LEVEL_COLORS[level]} text-white text-xs`}>
                        {level}
                      </Badge>
                      <span className="text-xs text-gray-500">({users.length})</span>
                    </div>
                    <div className="pl-2 space-y-1">
                      {users.slice(0, 5).map(user => (
                        <p key={user.user_id} className="text-xs text-[#008F11] truncate">
                          {user.name || user.email}
                        </p>
                      ))}
                      {users.length > 5 && (
                        <p className="text-xs text-gray-500">+{users.length - 5} more</p>
                      )}
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminMonetizationPage;
